/**
 * KbdChord — keyboard-shortcut chord renderer.
 *
 * Splits a chord string (e.g. `"Meta+Shift+K"`) into a sequence of
 * `<kbd>` elements joined by a typographic plus glyph. Resolves
 * platform-specific labels so the rendered chord matches what the user
 * actually sees on their keyboard: `Meta` becomes `⌘` on macOS and
 * `Ctrl` elsewhere; `Alt` becomes `⌥` on macOS and `Alt` elsewhere.
 *
 * Single responsibility: render the chord. Listening for the chord
 * lives elsewhere — see {@link useCommandPaletteShortcut} for a
 * concrete example.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { GRAY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface KbdChordProps {
  /** Plus-separated chord string, e.g. `"Meta+Shift+K"`. */
  readonly chord: string;
  /** Optional override for macOS detection. */
  readonly platform?: "mac" | "other";
  /**
   * Separator glyph rendered between keys. Defaults to a thin spaced
   * plus so the chord reads as a single visual unit.
   */
  readonly separator?: React.ReactNode;
  /** Accessible label override. Defaults to the original chord string. */
  readonly ariaLabel?: string;
  /** Additional className applied to the wrapper. */
  readonly className?: string;
}

const KEY_LABEL_MAC: Readonly<Record<string, string>> = {
  meta: "⌘",
  cmd: "⌘",
  command: "⌘",
  ctrl: "⌃",
  control: "⌃",
  alt: "⌥",
  option: "⌥",
  shift: "⇧",
  enter: "⏎",
  return: "⏎",
  esc: "⎋",
  escape: "⎋",
  tab: "⇥",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
  backspace: "⌫",
  delete: "⌦",
  space: "Space",
};

const KEY_LABEL_OTHER: Readonly<Record<string, string>> = {
  meta: "Ctrl",
  cmd: "Ctrl",
  command: "Ctrl",
  ctrl: "Ctrl",
  control: "Ctrl",
  alt: "Alt",
  option: "Alt",
  shift: "Shift",
  enter: "Enter",
  return: "Enter",
  esc: "Esc",
  escape: "Esc",
  tab: "Tab",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
  backspace: "Backspace",
  delete: "Delete",
  space: "Space",
};

function detectPlatform(): "mac" | "other" {
  if (typeof navigator === "undefined") return "other";
  const platform = navigator.platform.toLowerCase();
  return platform.includes("mac") ? "mac" : "other";
}

function renderKey(rawKey: string, platform: "mac" | "other"): string {
  const lookup = platform === "mac" ? KEY_LABEL_MAC : KEY_LABEL_OTHER;
  const lowered = rawKey.toLowerCase();
  const mapped = lookup[lowered];
  if (mapped !== undefined) return mapped;
  if (rawKey.length === 1) return rawKey.toUpperCase();
  return rawKey;
}

const KBD_STYLE: React.CSSProperties = {
  background: GRAY[100],
  border: `1px solid ${GRAY[300]}`,
  borderBottomWidth: 2,
  borderRadius: "0.25rem",
  color: GRAY[900],
  display: "inline-block",
  fontFamily: "ui-monospace, monospace",
  fontSize: "0.7rem",
  fontWeight: 600,
  lineHeight: 1,
  minWidth: "1.4em",
  padding: "0.15rem 0.4rem",
  textAlign: "center",
};

/** Renders a keyboard chord with platform-appropriate key labels. */
export const KbdChord = React.memo(function KbdChord({
  chord,
  platform,
  separator = "+",
  ariaLabel,
  className,
}: KbdChordProps): React.JSX.Element {
  const resolvedPlatform = platform ?? detectPlatform();
  const keys = useMemo(
    () =>
      chord
        .split("+")
        .map((key) => key.trim())
        .filter((key) => key.length > 0),
    [chord],
  );

  return (
    <span
      role="img"
      aria-label={ariaLabel ?? chord}
      data-csk-kbd-chord
      className={cn("inline-flex items-center", className)}
      style={{ gap: "0.2rem" }}
    >
      {keys.map((key, index) => (
        <React.Fragment key={`${key}-${index}`}>
          {index > 0 && (
            <span aria-hidden="true" style={{ color: GRAY[500], fontSize: "0.7rem" }}>
              {separator}
            </span>
          )}
          <kbd style={KBD_STYLE}>{renderKey(key, resolvedPlatform)}</kbd>
        </React.Fragment>
      ))}
    </span>
  );
});
